import { z } from 'zod';
import { UPLOAD_TYPES } from './artifacts.js';
import { MetaSchema } from './create-chat.js';

/**
 * Platform-staff permissions + the admin Evals surface (ADR-0072).
 *
 * A permission is an `<resource>:<action>` string — the unit a future
 * IAM-style policy grants. Today a staff member's grants are stored as a flat
 * list on their `PLATFORM#STAFF` row; a policy layer later maps roles onto
 * these same strings, so nothing that checks a permission has to change.
 *
 * `data` is strict (the BFF authors it); envelopes are loose (ADR-0024 §11).
 */

/** Every permission the platform knows. The BFF drops stored grants not listed here. */
export const PERMISSIONS = ['evals:read', 'evals:write'] as const;
export type Permission = (typeof PERMISSIONS)[number];

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** `data` of `GET /api/v1/me` — what the SPA may show. The server still enforces. */
export const StaffMeDataSchema = z.strictObject({
  permissions: z.array(z.enum(PERMISSIONS)),
});
export type StaffMeData = z.infer<typeof StaffMeDataSchema>;

export const StaffMeResponseSchema = z.looseObject({ data: StaffMeDataSchema, meta: MetaSchema });
export type StaffMeResponse = z.infer<typeof StaffMeResponseSchema>;

// ─── Evals (apps/workstreamagent/api/evals/run.py writes these) ─────────────

export const EVAL_STATUSES = ['pass', 'fail', 'review', 'known_gap', 'error', 'skipped'] as const;
export type EvalStatus = (typeof EVAL_STATUSES)[number];

const count = z.number().int().nonnegative();

export const EvalTotalsSchema = z.strictObject({
  pass: count,
  fail: count,
  review: count,
  known_gap: count,
  error: count,
  skipped: count,
});

/** One run's header — the list view reads only this (the report blob stays unread). */
export const EvalRunSummarySchema = z.strictObject({
  runId: z.string(),
  startedAt: z.string(),
  finishedAt: z.string(),
  target: z.string(),
  dataset: z.string(),
  gitSha: z.string(),
  totals: EvalTotalsSchema,
});
export type EvalRunSummary = z.infer<typeof EvalRunSummarySchema>;

const EvalTranscriptTurnSchema = z.strictObject({
  user: z.string(),
  agent: z.string(),
  reply: z.string(),
  modelCalls: count.nullable().optional(),
});

/** The rubric judge's verdict on one run: criterion → 0..1 score + a one-line reason. */
const EvalJudgeVerdictSchema = z.strictObject({
  scores: z.record(z.string(), z.number()),
  reasons: z.record(z.string(), z.string()),
  error: z.string().optional(),
});

const EvalCaseRunSchema = z.strictObject({
  rep: count.optional(),
  workstreamId: z.string(),
  failures: z.array(z.string()),
  error: z.string().optional(),
  durationS: z.number().optional(),
  transcript: z.array(EvalTranscriptTurnSchema).optional(),
  judge: EvalJudgeVerdictSchema.optional(),
});

/** One criterion's spread across a case's repeats (`--repeat N`). */
const EvalJudgeStatSchema = z.strictObject({ mean: z.number(), stdev: z.number(), n: count });

export const EvalCaseResultSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  feature: z.string(),
  layer: z.string(),
  status: z.enum(EVAL_STATUSES),
  set: z.string().optional(),
  tags: z.array(z.string()).optional(),
  judge: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  reason: z.string().optional(),
  passRate: z.number().nullable().optional(),
  durationS: z.number().optional(),
  judgeStats: z.record(z.string(), EvalJudgeStatSchema).optional(),
  runs: z.array(EvalCaseRunSchema),
});
export type EvalCaseResult = z.infer<typeof EvalCaseResultSchema>;

/** `data` of `GET /api/v1/admin/evals/runs`, newest first. */
export const EvalRunListDataSchema = z.strictObject({ runs: z.array(EvalRunSummarySchema) });
export type EvalRunListData = z.infer<typeof EvalRunListDataSchema>;

export const EvalRunListResponseSchema = z.looseObject({
  data: EvalRunListDataSchema,
  meta: MetaSchema,
});

/** `data` of `GET /api/v1/admin/evals/runs/{runId}`. */
export const EvalRunDataSchema = EvalRunSummarySchema.extend({
  model: z.string(),
  judgeModel: z.string().nullable().optional(),
  /** The judge score a rubric case had to reach this run (null with --no-judge). */
  passScore: z.number().nullable().optional(),
  repeat: count.optional(),
  tags: z.array(z.string()).optional(),
  cases: z.array(EvalCaseResultSchema),
});
export type EvalRunData = z.infer<typeof EvalRunDataSchema>;

export const EvalRunResponseSchema = z.looseObject({ data: EvalRunDataSchema, meta: MetaSchema });

/**
 * A run id as the runner mints it: `YYYYMMDDTHHMMSSZ-<8 hex>`. Time-prefixed so
 * the `RUN#{runId}` sort key orders chronologically; validated before it keys
 * a DynamoDB read.
 */
export const EVAL_RUN_ID = /^\d{8}T\d{6}Z-[0-9a-f]{8}$/;

// ─── Eval fixtures: question sets + documents (ADR-0073) ────────────────────
//
// Both live in the eval-fixtures bucket (`sets/<name>.json`, `documents/<file>`).
// The Evals page edits them through the BFF (`evals:write`); `evals/run.py --sync`
// mirrors them. The set grammar is the runner's. These schemas validate the
// SHAPE and the bounds at the trust boundary, and the runner stays the authority
// on `expect` semantics. Cases and `expect` are loose on purpose so a field the
// page doesn't edit (`state`, `stages`, `source`…) round-trips untouched.

/** A set name is its S3 key stem and its identity. */
export const EVAL_SET_NAME = /^[a-z0-9][a-z0-9-]{0,62}$/;
/** A document name is its S3 key leaf: no path, no leading dot, an uploadable extension. */
export const EVAL_DOCUMENT_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const EVAL_TAG = /^[a-z0-9][a-z0-9-]{0,31}$/;
/** Text documents the page may edit in place; anything bigger (or a PDF) is re-uploaded. */
export const MAX_EVAL_TEXT_DOCUMENT_BYTES = 1_000_000;
/** A whole set body — S3 would take more, but the page and the runner read it whole. */
export const MAX_EVAL_SET_BYTES = 1_000_000;

/** The upload content type for a document name, or undefined if it isn't uploadable. */
export function evalDocumentContentType(name: string): keyof typeof UPLOAD_TYPES | undefined {
  const lower = name.toLowerCase();
  return (Object.keys(UPLOAD_TYPES) as (keyof typeof UPLOAD_TYPES)[]).find((type) =>
    (UPLOAD_TYPES[type].extensions as readonly string[]).some((ext) => lower.endsWith(ext)),
  );
}

export function isEvalDocumentName(name: string): boolean {
  return EVAL_DOCUMENT_NAME.test(name) && evalDocumentContentType(name) !== undefined;
}

const tags = z.array(z.string().regex(EVAL_TAG)).max(20);
const EvalTurnSchema = z.looseObject({ message: z.string().min(1).max(8_000) });

export const EvalCaseSchema = z
  .looseObject({
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/),
    name: z.string().min(1).max(200),
    tags: tags.optional(),
    layer: z.string().max(32).optional(),
    notes: z.string().max(4_000).nullable().optional(),
    inputs: z.looseObject({
      setup: z
        .looseObject({
          turns: z.array(EvalTurnSchema).max(20).optional(),
          documents: z
            .array(z.string().refine(isEvalDocumentName, 'not an uploadable document name'))
            .max(10)
            .optional(),
        })
        .optional(),
      // Optional only for `e2e` cases (a Playwright journey the runner skips) —
      // the refine below requires at least one turn for everything else.
      turns: z.array(EvalTurnSchema).max(20).optional(),
    }),
    expect: z.looseObject({
      reply_matches: z.array(z.string().max(500)).max(20).optional(),
      reply_not_matches: z.array(z.string().max(500)).max(20).optional(),
      judge: z.string().max(4_000).optional(),
      /** The judge score this case must reach (ADR-0073 §5); default: the run's --pass-score. */
      pass_score: z.number().min(0).max(1).optional(),
    }),
  })
  .refine((c) => c.layer === 'e2e' || (c.inputs.turns?.length ?? 0) > 0, {
    message: 'a case needs at least one user message',
    path: ['inputs', 'turns'],
  });
export type EvalCase = z.infer<typeof EvalCaseSchema>;

export const EvalSetSchema = z
  .looseObject({
    name: z.string().regex(EVAL_SET_NAME),
    description: z.string().max(2_000).optional(),
    tags: tags.optional(),
    cases: z.array(EvalCaseSchema).max(200),
  })
  .refine((set) => new Set(set.cases.map((c) => c.id)).size === set.cases.length, {
    message: 'case ids must be unique within a set',
    path: ['cases'],
  });
export type EvalSet = z.infer<typeof EvalSetSchema>;

/** A set in the list view — the header only, never the cases. */
export const EvalSetSummarySchema = z.strictObject({
  name: z.string(),
  sizeBytes: count,
  lastModified: z.string(),
});
export type EvalSetSummary = z.infer<typeof EvalSetSummarySchema>;

export const EvalSetListDataSchema = z.strictObject({ sets: z.array(EvalSetSummarySchema) });
export type EvalSetListData = z.infer<typeof EvalSetListDataSchema>;
export const EvalSetListResponseSchema = z.looseObject({
  data: EvalSetListDataSchema,
  meta: MetaSchema,
});

/**
 * `data` of `GET /admin/evals/sets/{name}`. `etag` goes back on the save as
 * `If-Match`, so two people editing one set get a 409 instead of a silent
 * last-write-wins.
 */
export const EvalSetDataSchema = z.strictObject({ set: EvalSetSchema, etag: z.string() });
export type EvalSetData = z.infer<typeof EvalSetDataSchema>;
export const EvalSetResponseSchema = z.looseObject({ data: EvalSetDataSchema, meta: MetaSchema });

/** Body of `PUT /admin/evals/sets/{name}`: `etag` null = create (fails if it exists). */
export const PutEvalSetRequestSchema = z.strictObject({
  set: EvalSetSchema,
  etag: z.string().max(200).nullable(),
});
export type PutEvalSetRequest = z.infer<typeof PutEvalSetRequestSchema>;

export const EvalDocumentSchema = z.strictObject({
  name: z.string(),
  sizeBytes: count,
  lastModified: z.string(),
});
export type EvalDocument = z.infer<typeof EvalDocumentSchema>;

export const EvalDocumentListDataSchema = z.strictObject({
  documents: z.array(EvalDocumentSchema),
});
export type EvalDocumentListData = z.infer<typeof EvalDocumentListDataSchema>;
export const EvalDocumentListResponseSchema = z.looseObject({
  data: EvalDocumentListDataSchema,
  meta: MetaSchema,
});

/** `data` of `GET /admin/evals/documents/{name}` — text documents only. */
export const EvalDocumentTextDataSchema = z.strictObject({
  name: z.string(),
  text: z.string(),
  etag: z.string(),
});
export type EvalDocumentTextData = z.infer<typeof EvalDocumentTextDataSchema>;
export const EvalDocumentTextResponseSchema = z.looseObject({
  data: EvalDocumentTextDataSchema,
  meta: MetaSchema,
});

/** Body of `PUT /admin/evals/documents/{name}` — an in-place edit of a text document. */
export const PutEvalDocumentTextRequestSchema = z.strictObject({
  text: z.string(),
  etag: z.string().max(200),
});

/** Body of `POST /admin/evals/documents/uploads` → a presigned POST for the browser. */
export const BeginEvalDocumentUploadRequestSchema = z.strictObject({
  name: z.string().refine(isEvalDocumentName, 'not an uploadable document name'),
});
export const BeginEvalDocumentUploadDataSchema = z.strictObject({
  url: z.string(),
  fields: z.record(z.string(), z.string()),
});
export type BeginEvalDocumentUploadData = z.infer<typeof BeginEvalDocumentUploadDataSchema>;
export const BeginEvalDocumentUploadResponseSchema = z.looseObject({
  data: BeginEvalDocumentUploadDataSchema,
  meta: MetaSchema,
});
